import NotStartedIcon from "@mui/icons-material/NotStarted";
import { Box, Container, IconButton, Tooltip, Typography } from "@mui/material";
import ConfirmDialog from "components/ConfirmDialog";
import SnackbarMessage from "components/SnackbarMessage";
import ExpandableTable, {
  TableSortType,
  TableCellType,
  LazyTableRow,
} from "components/ExpandableTable";
import { hasUserPermission } from "helpers/ClaimHelpers";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage as T, useIntl } from "react-intl";
import authStore from "stores/authStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { ClaimPermission, ClaimResource, ParentClaim } from "types/claimTypes";
import { ProcessTypeIds } from "types/common-types";
import { resolveFlushFeedback } from "pages/ProcessesPage/flushFeedback";
import AuditLogsContent from "./AuditLogsContent";

const FLUSH_PROBLEM_SNACKBAR_DURATION = 10000;

type ProcessRow = LazyTableRow & {
  processName: string;
  description: string;
  onClick: () => void;
  processTypeId?: string;
};

const ProcessesList = () => {
  const { formatMessage: t } = useIntl();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("sequence");
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  useListTitle("processesListLabel");

  const navigate = useNavigate();
  const { addExportCoursesUrl } = useGlobalContext();
  const { isLoggedIn, claims } = authStore.useState((state) => ({
    isLoggedIn: state.isLoggedIn,
    claims: state.claims,
  }));

  const [buildCatalogDialogOpen, buildCatalogDialogOpenActions] = useBoolean(
    false,
  );
  const [syncCatalogDialogOpen, syncCatalogDialogOpenActions] = useBoolean(
    false,
  );
  const [
    updateEvaluationsDialogOpen,
    updateEvaluationsDialogOpenActions,
  ] = useBoolean(false);
  const [
    syncOrganizationsDialogOpen,
    syncOrganizationsDialogOpenActions,
  ] = useBoolean(false);
  const [
    flushRedisCacheDialogOpen,
    flushRedisCacheDialogOpenActions,
  ] = useBoolean(false);
  const [
    resyncSettingsCacheDialogOpen,
    resyncSettingsCacheDialogOpenActions,
  ] = useBoolean(false);
  const [
    resyncDesignationsDialogOpen,
    resyncDesignationsDialogOpenActions,
  ] = useBoolean(false);
  const [
    resyncLocationsDialogOpen,
    resyncLocationsDialogOpenActions,
  ] = useBoolean(false);
  const [
    resyncLicensureBodiesDialogOpen,
    resyncLicensureBodiesDialogOpenActions,
  ] = useBoolean(false);

  const processActions = ({ value }: { value: ProcessRow }) => {
    return (
      <Box justifyContent="center" display="flex">
        <Tooltip title={value.processName}>
          <span>
            <IconButton size="small" onClick={value.onClick}>
              <NotStartedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "processName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "processTitle" }),
      noSort: true,
    },
    {
      accessor: "description",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "descriptionLabel" }),
      noSort: true,
    },
    {
      accessor: "action",
      align: "center",
      width: 100,
      type: "node",
      View: processActions,
      label: t({ id: "actionTitle" }),
      noSort: true,
    },
  ];

  const canResyncSettingsCache = hasUserPermission(
    ParentClaim.Operations,
    ClaimResource.Processes,
    ClaimPermission.SettingSync,
  );
  const canResyncDesignations = hasUserPermission(
    ParentClaim.Operations,
    ClaimResource.Processes,
    ClaimPermission.DesignationSync,
  );
  const canResyncLocations = hasUserPermission(
    ParentClaim.Operations,
    ClaimResource.Processes,
    ClaimPermission.LocationSync,
  );
  const canResyncLicensureBodies = hasUserPermission(
    ParentClaim.Operations,
    ClaimResource.Processes,
    ClaimPermission.LicensureBodySync,
  );
  const canExportScormCourses =
    isLoggedIn &&
    (claims?.some((claim) => claim?.claimResource === ClaimResource.Rustici) ??
      false);

  // Define a hard-coded data for the table, with the list of processes of the system
  const data: ProcessRow[] = [
    {
      id: "1",
      processName: "Sync Catalog from CRM",
      description:
        "Sync SKUs from CRM, so they become available in KONE. This will also start a Build Catalog process after the sync completes.",
      onClick: () => {
        syncCatalogDialogOpenActions.setTrue();
      },
      isExpandable: true,
      processTypeId: ProcessTypeIds.SyncCatalogFromCRM,
      renderExpandedData: () => (
        <AuditLogsContent processTypeId={ProcessTypeIds.SyncCatalogFromCRM} />
      ),
    },
    {
      id: "2",
      processName: "Build Catalog",
      description:
        "Rebuild Catalog, Memberships, and Packages contents based on the rules defined.",
      onClick: () => {
        buildCatalogDialogOpenActions.setTrue();
      },
      isExpandable: true,
      processTypeId: ProcessTypeIds.BuildCatalog,
      renderExpandedData: () => (
        <AuditLogsContent processTypeId={ProcessTypeIds.BuildCatalog} />
      ),
    },
    {
      id: "3",
      processName: "Update Evaluations",
      description: "Update the cache that connects Evaluations and SKUs.",
      onClick: () => {
        updateEvaluationsDialogOpenActions.setTrue();
      },
      isExpandable: true,
      processTypeId: ProcessTypeIds.UpdateEvaluations,
      renderExpandedData: () => (
        <AuditLogsContent processTypeId={ProcessTypeIds.UpdateEvaluations} />
      ),
    },
    ...(canExportScormCourses
      ? [
          {
            id: "11",
            processName: "SCORM Course Export",
            description: t({ id: "scormCourseExportDescription" }),
            onClick: () => {
              navigate(addExportCoursesUrl());
            },
            isExpandable: true,
            processTypeId: ProcessTypeIds.ScormCourseExport,
            renderExpandedData: () => (
              <AuditLogsContent
                processTypeId={ProcessTypeIds.ScormCourseExport}
              />
            ),
          },
        ]
      : []),
    {
      id: "4",
      processName: "Sync Organizations",
      description:
        "A process to sync Organizations. Should generally not be necessary to run this process.",
      onClick: () => {
        syncOrganizationsDialogOpenActions.setTrue();
      },
    },
    {
      id: "5",
      processName: "Flush Redis Cache",
      description:
        "This will clear the full KONE cache server, this can have performance implications.",
      onClick: () => {
        flushRedisCacheDialogOpenActions.setTrue();
      },
    },
    ...(canResyncSettingsCache
      ? [
          {
            id: "6",
            processName: "Resync Settings Cache",
            description:
              "Rebuild the settings cache for all organizations. Use this if settings cache values are missing or incorrect.",
            onClick: () => {
              resyncSettingsCacheDialogOpenActions.setTrue();
            },
          },
        ]
      : []),
    ...(canResyncDesignations
      ? [
          {
            id: "7",
            processName: "Resync Designations",
            description:
              "Sync Designations from the Certificate master table to all dependent service caches.",
            onClick: () => {
              resyncDesignationsDialogOpenActions.setTrue();
            },
          },
        ]
      : []),
    ...(canResyncLocations
      ? [
          {
            id: "9",
            processName: "Resync Locations",
            description:
              "Sync Locations from the Certificate master table to all dependent service caches.",
            onClick: () => {
              resyncLocationsDialogOpenActions.setTrue();
            },
          },
        ]
      : []),
    ...(canResyncLicensureBodies
      ? [
          {
            id: "10",
            processName: "Resync Licensure Bodies",
            description:
              "Sync Licensure Bodies from the Certificate master table to all dependent service caches.",
            onClick: () => {
              resyncLicensureBodiesDialogOpenActions.setTrue();
            },
          },
        ]
      : []),
  ];

  const {
    catalogService,
    evaluationService,
    organizationService,
    redisCacheService,
    settingService,
    certificateEntityResyncService,
  } = useGlobalContext();

  const buildCatalog = async () => {
    await catalogService.buildCatalogs();
    buildCatalogDialogOpenActions.setFalse();
  };

  const syncCatalog = async () => {
    await catalogService.syncCatalogs();
    syncCatalogDialogOpenActions.setFalse();
  };

  const updateEvaluations = async () => {
    await evaluationService.updateEvaluationCache();
    updateEvaluationsDialogOpenActions.setFalse();
  };

  const syncOrganizations = async () => {
    await organizationService.syncOrganizations();
    syncOrganizationsDialogOpenActions.setFalse();
  };

  const flushRedisCache = async () => {
    const result = await redisCacheService.flushCache();
    flushRedisCacheDialogOpenActions.setFalse();

    const { messageId, severity } = resolveFlushFeedback(
      result?.nodesFlushed,
      result?.nodesTotal,
    );

    setSnackbarMessage(
      new SnackbarMessageProps(
        t(
          { id: messageId },
          { flushed: result?.nodesFlushed, total: result?.nodesTotal },
        ),
        severity,
        severity === Severity.Success
          ? undefined
          : FLUSH_PROBLEM_SNACKBAR_DURATION,
      ),
    );
  };

  const resyncSettingsCache = async () => {
    await settingService.resyncSettingsCache();
    resyncSettingsCacheDialogOpenActions.setFalse();
  };

  const resyncDesignations = async () => {
    await certificateEntityResyncService.resyncDesignations();
    resyncDesignationsDialogOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "resyncDesignationsTriggered" }),
        Severity.Success,
      ),
    );
  };

  const resyncLocations = async () => {
    await certificateEntityResyncService.resyncLocations();
    resyncLocationsDialogOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "resyncLocationsTriggered" }),
        Severity.Success,
      ),
    );
  };

  const resyncLicensureBodies = async () => {
    await certificateEntityResyncService.resyncLicensureBodies();
    resyncLicensureBodiesDialogOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "resyncLicensureBodiesTriggered" }),
        Severity.Success,
      ),
    );
  };

  return (
    <Container>
      <ExpandableTable
        {...{
          rowCount: data?.length ?? 0,
          rows: data ?? [],
          loading: false,
          allowDragAndDrop: false,
          dense: true,
          titleComponent: (
            <Typography variant="h6">
              <T id="processesListLabel" />
            </Typography>
          ),
          cells,
          allowPagination: false,
          page,
          setPage,
          rowsPerPage,
          setRowsPerPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          collapsable: true,
        }}
      />

      <ConfirmDialog
        dialogTitleKey="buildCatalog"
        contentKey="buildCatalogWarning"
        open={buildCatalogDialogOpen}
        onCancel={buildCatalogDialogOpenActions.setFalse}
        handleConfirm={buildCatalog}
        confirmLabel="buildCatalog"
      />

      <ConfirmDialog
        dialogTitleKey="syncCatalogWithCrm"
        contentKey="syncCatalogWarning"
        open={syncCatalogDialogOpen}
        onCancel={syncCatalogDialogOpenActions.setFalse}
        handleConfirm={syncCatalog}
        confirmLabel="syncCatalog"
      />

      <ConfirmDialog
        dialogTitleKey="updateEvaluations"
        contentKey="updateEvaluationsWarning"
        open={updateEvaluationsDialogOpen}
        onCancel={updateEvaluationsDialogOpenActions.setFalse}
        handleConfirm={updateEvaluations}
        confirmLabel="updateEvaluations"
      />

      <ConfirmDialog
        dialogTitleKey="syncOrganizations"
        contentKey="syncOrganizationsWarning"
        open={syncOrganizationsDialogOpen}
        onCancel={syncOrganizationsDialogOpenActions.setFalse}
        handleConfirm={syncOrganizations}
        confirmLabel="syncOrganizations"
      />

      <ConfirmDialog
        dialogTitleKey="flushRedisCache"
        contentKey="flushRedisCacheWarning"
        open={flushRedisCacheDialogOpen}
        onCancel={flushRedisCacheDialogOpenActions.setFalse}
        handleConfirm={flushRedisCache}
        confirmLabel="flushRedisCache"
      />

      {canResyncSettingsCache && (
        <ConfirmDialog
          dialogTitleKey="resyncSettingsCache"
          contentKey="resyncSettingsCacheWarning"
          open={resyncSettingsCacheDialogOpen}
          onCancel={resyncSettingsCacheDialogOpenActions.setFalse}
          handleConfirm={resyncSettingsCache}
          confirmLabel="resyncSettingsCache"
        />
      )}

      {canResyncDesignations && (
        <ConfirmDialog
          dialogTitleKey="resyncDesignations"
          contentKey="resyncDesignationsWarning"
          open={resyncDesignationsDialogOpen}
          onCancel={resyncDesignationsDialogOpenActions.setFalse}
          handleConfirm={resyncDesignations}
          confirmLabel="resyncDesignations"
        />
      )}

      {canResyncLocations && (
        <ConfirmDialog
          dialogTitleKey="resyncLocations"
          contentKey="resyncLocationsWarning"
          open={resyncLocationsDialogOpen}
          onCancel={resyncLocationsDialogOpenActions.setFalse}
          handleConfirm={resyncLocations}
          confirmLabel="resyncLocations"
        />
      )}

      {canResyncLicensureBodies && (
        <ConfirmDialog
          dialogTitleKey="resyncLicensureBodies"
          contentKey="resyncLicensureBodiesWarning"
          open={resyncLicensureBodiesDialogOpen}
          onCancel={resyncLicensureBodiesDialogOpenActions.setFalse}
          handleConfirm={resyncLicensureBodies}
          confirmLabel="resyncLicensureBodies"
        />
      )}
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default ProcessesList;
